"use client";

import { useTranslations } from "next-intl";
import * as React from "react";

import EmptyStateMessage from "@/components/ui/emptyStateMessage";
import { DEADLINE_OPTIONS } from "@/constants";
import {
  homeDeadlineSummaryColorMap,
  homeTagSolidColorClassMap,
} from "@/features/home/utils/homeTagColors";
import type {
  AviDeadlineOutput,
  DeadlineOutput,
  DeadlineStatus,
  DeadlineSummaryStatus,
} from "@/types";

interface DeadlineItem {
  status: DeadlineSummaryStatus;
  count: number;
  percentage: number;
}

function getDeadlineItems(data: DeadlineOutput | AviDeadlineOutput): DeadlineItem[] {
  const deadlineItems: DeadlineItem[] = DEADLINE_OPTIONS.map((status: DeadlineStatus) => ({
    status,
    count: data[status],
    percentage: data[`${status}_pct`],
  }));

  if (!("completed" in data)) {
    return deadlineItems;
  }

  return [
    ...deadlineItems,
    {
      status: "completed",
      count: data.completed,
      percentage: data.completed_pct,
    },
  ];
}

interface DeadlineCardProps {
  data: DeadlineOutput | AviDeadlineOutput | undefined;
  icon: React.ReactNode;
}

export function DeadlineCard({ data, icon }: DeadlineCardProps) {
  const t = useTranslations("home");
  const tGlossary = useTranslations("glossary");
  const isEmpty = !data || data.total === 0;

  const items = data && data.total > 0 ? getDeadlineItems(data) : [];

  return (
    <div className="flex w-full flex-1 flex-col gap-2">
      {isEmpty ? (
        <div className="flex h-35.75 items-center justify-center">
          <EmptyStateMessage
            message={t("empty.deadline")}
            icon={<span className="color-neutral-950">{icon}</span>}
            compact
          />
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <div className="flex size-6 shrink-0 flex-row items-center justify-center gap-[5.54px] rounded-lg bg-[#D5F2F0] p-0">
              <span className="text-[#1A1A1A] [&_svg]:size-[14px]">{icon}</span>
            </div>
            <span className="heading-6 translate-y-0.5 text-neutral-950">
              {t("section.deadline")}
            </span>
          </div>

          <div className="flex flex-col rounded-md bg-neutral-50">
            <div className="flex w-full px-4 pt-3 pb-2 xl:pt-4 xl:pb-3">
              {items.map((item) => (
                <div
                  key={item.status}
                  className={`h-2 rounded-3xl ${homeTagSolidColorClassMap[homeDeadlineSummaryColorMap[item.status]]}`}
                  style={{ flex: item.percentage }}
                />
              ))}
            </div>

            <div className="border-t border-neutral-100" />

            <div className="flex items-center justify-between gap-2 px-4 py-2 xl:py-3">
              {items.map((item) => (
                <div key={item.status} className="flex w-fit items-start gap-1">
                  <div className="flex size-4 shrink-0 items-center justify-center">
                    <div
                      className={`size-2 rounded-full ${homeTagSolidColorClassMap[homeDeadlineSummaryColorMap[item.status]]}`}
                    />
                  </div>
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="font-body truncate text-[10px] leading-4 text-neutral-950 2xl:text-xs">
                      {tGlossary(`deadlineStatus.${item.status}`)}
                    </span>
                    <span className="font-body text-[10px] leading-4 text-neutral-600 2xl:text-xs">
                      {item.count} ({Math.round(item.percentage)}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
